import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import isotipo from "@/assets/isotipo.svg";
import { BrandMark } from "@/shared/ui/core";

export interface SidebarBrandProps {
  collapsed?: boolean;
  /** A dónde vuelve la marca. */
  to?: string;
  className?: string;
}

/**
 * La marca, arriba de la barra lateral.
 *
 * Sin serif itálica: Anahí la retiró de todo el sistema. La jerarquía la
 * sostienen el peso y el tamaño de la sans —"New Horizons" en semibold
 * con el tracking apretado, "It's The Best" en 10 px con el tracking
 * ancho—, no un cambio de familia tipográfica.
 *
 * Plegada, el nombre completo no cabe y queda solo el isotipo sobre su
 * tesela de vidrio. El nombre accesible del enlace NO cambia con el
 * plegado: quien navega con lector de pantalla oye lo mismo en los dos
 * estados.
 *
 * El isotipo se pinta con MÁSCARA CSS y no con un `<img>`: así el color
 * sale de `--violet-pop` y sigue a la marca si la paleta cambia, sin
 * tener que reexportar el SVG. Es la misma técnica que usa la card del
 * login para el logotipo completo. Por eso es un `<span>` vacío y
 * `aria-hidden`: no hay contenido que leer, y el nombre accesible ya lo
 * pone el enlace que lo envuelve.
 */
export function SidebarBrand({ collapsed = false, to = "/", className }: SidebarBrandProps) {
  const { t } = useTranslation();

  return (
    <Link
      to={to}
      aria-label={t("It's The Best")}
      className={cn(
        "flex min-w-0 items-center gap-2.5 rounded-2xl outline-none",
        "focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
        collapsed && "justify-center",
        className,
      )}
    >
      {collapsed && (
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl",
          "border border-ds-border bg-white/70",
        )}
      >
        <span
          className="h-5 w-5"
          style={{
            backgroundColor: "var(--violet-pop)",
            WebkitMaskImage: `url(${isotipo})`,
            maskImage: `url(${isotipo})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
            WebkitMaskSize: "contain",
            maskSize: "contain",
          }}
        />
      </span>
      )}

      {/* Desplegada: el imagotipo completo. El nombre del proyecto no se
          escribe al lado — la marca del sistema es It's The Best, y
          "New Horizons" es el nombre interno de esta reconstrucción, no
          algo que un usuario deba leer en su barra lateral. */}
      {!collapsed && <BrandMark height={26} label={null} className="min-w-0 shrink" />}
    </Link>
  );
}
